import type { VoiceNavPendingOpenModalityWorkflow } from '../../api/apiConfig'
import type { HostPatientNavigationAdapter } from '../../host/HostPatientNavigationAdapter'
import type { ChatMessage } from '../../types/chat'
import { hostOrSessionChartMatchesModalitySwitchTarget } from '../../utils/modalitySwitchHostSyncRecovery'
import type { ActivePatientSyncPayload, DeferredReplaySessionContext } from './types'
import {
  summarizePendingUiReplayResponse,
  type PendingUiReplayOutcome,
} from '../../utils/pendingUiReplayOutcome'
import {
  buildMergedActivePatientForDeferredReplay,
  shouldRetryDeferredReplayAfterFirstAttempt,
  shouldRunDeferredEcgReplayPipeline,
  type MutableRefLike,
} from './deferredEcgReplayCore'

export type ReplayAssistantRowsHandler = (rows: unknown[]) => void | Promise<void>
export type { PendingUiReplayOutcome }

/**
 * After host reload / widget hydrate: if a deferred ECG was armed, sync active patient then POST replay.
 * Exactly-once UX is enforced server-side; client skips duplicate POSTs via ``pendingUiReplayDoneRef``.
 */
export async function tryReplayDeferredPatientSwitchEcg(input: {
  adapter: HostPatientNavigationAdapter
  restoredMessages: readonly ChatMessage[]
  /** Context from the same hydrate pass as chat-history (may be null). */
  initialSessionContext: DeferredReplaySessionContext | null
  pendingUiReplayDoneRef: MutableRefLike<boolean>
  lastPersistedOpenPatientKeyRef: MutableRefLike<string | null>
  /** Optional: align with widget ``applyPendingUiReplayIfAny`` in-flight guard. */
  replayInFlightRef?: MutableRefLike<boolean>
  clearPatientSwitchUserCommitted: () => void
  onReplaySuccess: ReplayAssistantRowsHandler
  /** Called after each ``POST /session/pending-ui-replay`` (and when the pipeline is skipped). */
  onReplayAttempt?: (outcome: PendingUiReplayOutcome) => void
  /** After a successful ``POST /session/active-patient`` during replay (widget session chips). */
  onActivePatientSyncedAfterPersist?: (payload: ActivePatientSyncPayload) => void
  /** Always invoked when exiting (clears ``reloadModalityReplayStaged`` in the widget). */
  onFinishedPipeline: () => void
}): Promise<void> {
  const {
    adapter,
    restoredMessages,
    initialSessionContext,
    pendingUiReplayDoneRef,
    lastPersistedOpenPatientKeyRef,
    replayInFlightRef,
    clearPatientSwitchUserCommitted,
    onReplaySuccess,
    onReplayAttempt,
    onActivePatientSyncedAfterPersist,
    onFinishedPipeline,
  } = input

  const hostPatient = await adapter.getHostActivePatient()
  const ctx =
    initialSessionContext ?? (await adapter.fetchPersistedSessionContext())

  const shouldRun = shouldRunDeferredEcgReplayPipeline({
    restoredMessages,
    sessionContext: ctx,
    pendingUiReplayDone: pendingUiReplayDoneRef.current,
    consumeBrowserDeferredModalityHint: () => adapter.consumeBrowserDeferredModalityHint(),
    hostPatient,
  })

  if (!shouldRun) {
    onReplayAttempt?.({
      pipelineRan: false,
      pipelineSkipped: true,
      skipReason: 'no deferred modality / replay state after refresh',
      httpOk: true,
      replayed: false,
      assistantRowCount: 0,
      showModalityRowCount: 0,
      attempt: 0,
    })
    onFinishedPipeline()
    return
  }

  clearPatientSwitchUserCommitted()

  const wf = ctx?.pending_open_modality_workflow
  if (
    (wf?.kind === 'open_modality_for_patient' ||
      wf?.kind === 'view_prescription_for_patient' ||
      wf?.kind === 'sports_noc_for_patient' ||
      wf?.kind === 'renew_ordonnance_for_patient') &&
    wf.awaiting_switch_confirm
  ) {
    // Force ``POST /session/active-patient`` when Redis still has patient 1 but workflow targets patient 2.
    lastPersistedOpenPatientKeyRef.current = null
  }
  const merged = buildMergedActivePatientForDeferredReplay({
    hostPatient,
    restoredMessages,
    loadedSessionContext: ctx,
  })
  if (merged && ((merged.patient_id ?? '').trim() || (merged.patient_name ?? '').trim())) {
    const key = [merged.patient_id ?? '', merged.patient_name ?? '', merged.dob ?? ''].join('|')
    if (lastPersistedOpenPatientKeyRef.current !== key) {
      lastPersistedOpenPatientKeyRef.current = key
      const ok = await adapter.persistActivePatientForReplay(merged)
      if (!ok) {
        lastPersistedOpenPatientKeyRef.current = null
      } else {
        const ctxSynced = await adapter.fetchPersistedSessionContext()
        const ap = ctxSynced?.active_patient
        const payload =
          ap &&
          ((ap.patient_id || '').trim() ||
            (ap.patient_name || '').trim() ||
            (ap.patient_unique_id || '').trim())
            ? ap
            : merged
        onActivePatientSyncedAfterPersist?.(payload)
      }
    }
  }

  let replayAttempt = 0
  const runReplayOnce = async (): Promise<void> => {
    if (pendingUiReplayDoneRef.current) return
    if (replayInFlightRef?.current) return
    if (replayInFlightRef) replayInFlightRef.current = true
    replayAttempt += 1
    try {
      const replay = await adapter.requestPendingUiReplay()
      const outcome = summarizePendingUiReplayResponse(replay, replayAttempt)
      onReplayAttempt?.(outcome)
      if (!replay?.replayed || !Array.isArray(replay.assistant_messages)) return
      pendingUiReplayDoneRef.current = true
      await onReplaySuccess(replay.assistant_messages)
    } finally {
      if (replayInFlightRef) replayInFlightRef.current = false
    }
  }

  await runReplayOnce()

  if (!pendingUiReplayDoneRef.current) {
    const ctxAfter = await adapter.fetchPersistedSessionContext()
    if (
      shouldRetryDeferredReplayAfterFirstAttempt({
        sessionContextAfter: ctxAfter,
        pendingUiReplayDone: pendingUiReplayDoneRef.current,
      })
    ) {
      lastPersistedOpenPatientKeyRef.current = null
      const hostAfter = await adapter.getHostActivePatient()
      const mergedRetry = buildMergedActivePatientForDeferredReplay({
        hostPatient: hostAfter,
        restoredMessages,
        loadedSessionContext: ctxAfter,
      })
      if (
        mergedRetry &&
        ((mergedRetry.patient_id ?? '').trim() || (mergedRetry.patient_name ?? '').trim())
      ) {
        const key2 = [
          mergedRetry.patient_id ?? '',
          mergedRetry.patient_name ?? '',
          mergedRetry.dob ?? '',
        ].join('|')
        if (lastPersistedOpenPatientKeyRef.current !== key2) {
          lastPersistedOpenPatientKeyRef.current = key2
          const ok2 = await adapter.persistActivePatientForReplay(mergedRetry)
          if (!ok2) {
            lastPersistedOpenPatientKeyRef.current = null
          } else {
            const ctxSynced2 = await adapter.fetchPersistedSessionContext()
            const ap2 = ctxSynced2?.active_patient
            const payload2 =
              ap2 &&
              ((ap2.patient_id || '').trim() ||
                (ap2.patient_name || '').trim() ||
                (ap2.patient_unique_id || '').trim())
                ? ap2
                : mergedRetry
            onActivePatientSyncedAfterPersist?.(payload2)
          }
        }
      }
      await runReplayOnce()
    }
  }

  onFinishedPipeline()
}
